import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Edit2, X, Calendar, CheckCircle2 } from 'lucide-react';
import { ProjectCard, ProjectColumn } from '../../types/projects';
import { CardDetailModal } from './CardDetailModal';

interface CardProps {
  card: ProjectCard;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
  setColumns: React.Dispatch<React.SetStateAction<ProjectColumn[]>>;
}

export const Card: React.FC<CardProps> = ({
  card,
  index,
  onEdit,
  onDelete,
  readOnly = false,
  setColumns
}) => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const getLabelStyles = (label?: 'warning' | 'danger' | null) => {
    switch (label) {
      case 'warning':
        return 'border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'danger':
        return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20';
      default:
        return 'bg-white dark:bg-gray-700';
    }
  };

  const calculateProgress = () => {
    if (!card.tasks || card.tasks.length === 0) return 0;
    const completedTasks = card.tasks.filter(task => task.completed).length;
    return Math.round((completedTasks / card.tasks.length) * 100);
  };

  const progress = calculateProgress();

  return (
    <>
      <Draggable draggableId={card.id} index={index} isDragDisabled={readOnly}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`p-3 rounded shadow-sm hover:shadow-md transition-shadow cursor-pointer ${getLabelStyles(card.label)}`}
            onClick={() => setIsDetailModalOpen(true)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-white">{card.title}</h3>
                <div className="mt-2 space-y-2">
                  {card.tasks && card.tasks.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs mt-1">{progress}% complété</span>
                      </div>
                    </div>
                  )}
                  {card.dueDate && (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(card.dueDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-2 ml-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={onEdit}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onDelete}
                    className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Draggable>

      <CardDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        card={card}
        onEdit={onEdit}
        readOnly={readOnly}
        setColumns={setColumns}
      />
    </>
  );
};